// Modular Timeline Component
// Reusable timeline component that can be used across different domains

import { ReactElement, ReactNode } from 'react'
import { Box, Paper, Typography, Chip, useTheme } from '@mui/material'
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab'
import { Timeline as TimelineIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

// Generic timeline event interface (forward declaration)
export interface TimelineEventData {
  id: string
  title: string
  description?: string
  timestamp: string | null
  type: string
  metadata?: { key: string; value: string }[]
  color?: string
  isDownloadable?: boolean

  actions?: TimelineAction[]
  data?: any
}

// Timeline action interface
export interface TimelineAction {
  label: string
  icon?: ReactNode
  onClick: (event: TimelineEventData) => void
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
}

// Timeline group interface
export interface TimelineGroup {
  date: string
  events: TimelineEventData[]
  totalEvents: number
}

// Component props
export interface ModularTimelineProps {
  title?: string
  groups: TimelineGroup[]
  isLoading?: boolean
  emptyStateTitle?: string
  emptyStateDescription?: string
  onEventClick?: (event: TimelineEventData) => void
  maxHeight?: number
  showStats?: boolean
  showTitle?: boolean
  customActions?: TimelineAction[]
  renderEventContent?: (event: TimelineEventData) => ReactNode
}

// Individual timeline event component
interface TimelineEventItemProps {
  event: TimelineEventData
  onEventClick?: (event: TimelineEventData) => void
  renderContent?: (event: TimelineEventData) => ReactNode
}

let TimelineEventItem = ({
  event,
  onEventClick,
  renderContent,
}: TimelineEventItemProps): ReactElement => {
  let theme = useTheme()
  let eventColor = event.color || theme.palette.primary.main

  let handleClick = () => {
    if (onEventClick) {
      onEventClick(event)
    }
  }

  if (renderContent) {
    return <Box sx={{ mb: 2 }}>{renderContent(event)}</Box>
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          cursor: onEventClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': onEventClick
            ? {
                bgcolor: 'background.paperDark',
                borderColor: eventColor,
                transform: 'translateY(-2px)',
                boxShadow: theme.shadows[4],
              }
            : {},
        }}
        onClick={onEventClick ? handleClick : undefined}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              color="text.primary"
              variant="body2"
              sx={{
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {event.title}
            </Typography>
            {event.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
              >
                {event.description}
              </Typography>
            )}

            {/* Metadata chips */}
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <Chip
                label={event.type
                  .replace(/-/g, ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                size="medium"
                sx={{
                  bgcolor: `${eventColor}20`,
                  color: eventColor,
                  borderColor: eventColor,
                  fontWeight: 500,
                  fontSize: '0.7rem',
                  height: 20,
                }}
                variant="outlined"
              />
              {event.metadata?.slice(0, 2).map(({ key, value }) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="medium"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              ))}
            </Box>
          </Box>

          {/* Action Buttons */}
          {event.actions && event.actions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, ml: 2, flexShrink: 0 }}>
              {event.actions.map((action, index) => (
                <Chip
                  key={index}
                  label={action.label}
                  size="small"
                  color={action.color || 'primary'}
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation()
                    action.onClick(event)
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: `${theme.palette[action.color || 'primary'].main}20`,
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default function ModularTimeline({
  title = 'Timeline',
  groups,
  isLoading = false,
  emptyStateTitle = 'No Events Found',
  emptyStateDescription = 'No events match your current settings',
  onEventClick,
  maxHeight = 600,
  showStats = true,
  showTitle = true,
  renderEventContent,
}: ModularTimelineProps): ReactElement {
  let theme = useTheme()

  let totalEvents = groups.reduce(
    (total, group) => total + group.events.length,
    0,
  )

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading timeline data...
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper sx={{ p: 3, maxHeight, overflow: 'auto', position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        {showTitle && (
          <Typography
            variant="h6"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <TimelineIcon />
            {title}
          </Typography>
        )}
        {showStats && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              size="medium"
              label={`${groups.length} days`}
              variant="outlined"
            />
            <Chip
              size="medium"
              label={`${totalEvents} events`}
              color="primary"
            />
          </Box>
        )}
      </Box>
      {groups.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {emptyStateTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {emptyStateDescription}
          </Typography>
        </Box>
      ) : (
        <Timeline sx={{ '& .MuiTimelineContent-root': { pt: 1 } }}>
          {groups.map((group: TimelineGroup, groupIndex) =>
            group.events.map((event: TimelineEventData, eventIndex) => {
              let isLastInGroup = eventIndex === group.events.length - 1
              let isLastGroup = groupIndex === groups.length - 1
              let isLastEvent = isLastInGroup && isLastGroup

              return (
                <TimelineItem key={event.id}>
                  <TimelineOppositeContent sx={{ flex: 0.3 }}>
                    {eventIndex === 0 && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs(group.date).format('MMM D')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(group.date).format('dddd')}
                        </Typography>
                      </>
                    )}
                    <Typography
                      color="text.secondary"
                      variant="caption"
                      sx={{
                        mt: eventIndex === 0 ? 1 : 0,
                        display: 'block',
                      }}
                    >
                      {dayjs(event.timestamp).isValid()
                        ? dayjs(event.timestamp).format('h:mm A')
                        : 'Unknown time'}
                    </Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot
                      color="primary"
                      sx={{
                        bgcolor: event.color || theme.palette.primary.main,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.2)',
                        },
                      }}
                    />
                    {!isLastEvent && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pt: 1 }}>
                    <TimelineEventItem
                      event={event}
                      onEventClick={onEventClick}
                      renderContent={renderEventContent}
                    />
                  </TimelineContent>
                </TimelineItem>
              )
            }),
          )}
        </Timeline>
      )}
    </Paper>
  )
}
